import {
    AccountCircle,
    Assignment,
    CalendarToday,
    ChevronLeft,
    Dashboard,
    Description,
    EventAvailable,
    EventNote,
    LocalHospital,
    Logout,
    MedicalServices,
    Menu as MenuIcon,
    Payments,
    People,
    PersonAdd,
    Receipt,
    Schedule,
} from '@mui/icons-material';
import {
    AppBar,
    Avatar,
    Box,
    CssBaseline,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Toolbar,
    Typography,
    alpha,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { useAppSelector } from '../../../store/store';
import { isAdmin, isDoctor, isPatient } from '../../../types/auth';
import BrandMark from '@components/common/BrandMark';
import BrandWordmark from '@components/common/BrandWordmark';

const DRAWER_WIDTH = 272;

const DOCTOR_MENU = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/doctor/dashboard' },
    { text: 'Appointments', icon: <CalendarToday />, path: '/doctor/appointments' },
    { text: 'Patients', icon: <People />, path: '/doctor/patients' },
    { text: 'Availability', icon: <EventAvailable />, path: '/doctor/availability' },
    { text: 'Services', icon: <MedicalServices />, path: '/doctor/services' },
    { text: 'Clinics', icon: <LocalHospital />, path: '/doctor/clinics' },
    { text: 'Billing', icon: <Receipt />, path: '/doctor/billing' },
    { text: 'Payments', icon: <Payments />, path: '/doctor/payments' },
    { text: 'My Profile', icon: <AccountCircle />, path: '/doctor/profile' },
];

const PATIENT_MENU = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/patient/dashboard' },
    { text: 'My Appointments', icon: <CalendarToday />, path: '/patient/appointments' },
    { text: 'Medical Records', icon: <Description />, path: '/patient/medical-records' },
    { text: 'Billing & Invoices', icon: <Receipt />, path: '/patient/billing' },
    { text: 'Prescriptions', icon: <LocalHospital />, path: '/patient/prescriptions' },
    { text: 'My Profile', icon: <AccountCircle />, path: '/patient/profile' },
];

const ADMIN_MENU = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/admin/dashboard' },
    { text: 'Doctors', icon: <PersonAdd />, path: '/admin/doctors' },
    { text: 'Doctor Applications', icon: <Assignment />, path: '/admin/doctor-applications' },
    { text: 'Patients', icon: <People />, path: '/admin/patients' },
    { text: 'Appointments', icon: <EventNote />, path: '/admin/appointments' },
    { text: 'Jobs', icon: <Schedule />, path: '/admin/jobs' },
];

const Layout: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

    const [mobileOpen, setMobileOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const { user } = useAppSelector((state) => state.auth);
    const navigate = useNavigate();
    const location = useLocation();
    const { logout } = useAuth();

    const isUserPatient = isPatient(user);
    const isUserDoctor = isDoctor(user);
    const isUserAdmin = isAdmin(user);
    const menuItems = isUserAdmin ? ADMIN_MENU : isUserPatient ? PATIENT_MENU : DOCTOR_MENU;
    const appTitle = isUserAdmin ? 'Admin Panel' : isUserPatient ? 'Patient Portal' : 'Doctor Portal';
    const profilePath = isUserAdmin ? '/admin/dashboard' : isUserPatient ? '/patient/profile' : '/doctor/profile';

    const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);
    const handleLogout = () => { logout(); handleMenuClose(); };
    const handleNavigate = (path: string) => { navigate(path); if (isMobile) setMobileOpen(false); };

    const drawer = (
        <Box sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, transparent 40%)`,
        }}>
            {/* Logo */}
            <Toolbar sx={{ display: 'flex', alignItems: 'center', px: 2.5, gap: 1.5, minHeight: '68px !important' }}>
                <BrandMark size={36} radius={10} />
                <BrandWordmark height={20} />
                {isMobile && (
                    <IconButton onClick={handleDrawerToggle} sx={{ ml: 'auto' }}>
                        <ChevronLeft />
                    </IconButton>
                )}
            </Toolbar>

            <Box sx={{ px: 2, mb: 1 }}>
                <Divider sx={{ opacity: 0.6 }} />
            </Box>

            {/* Navigation */}
            <List sx={{ flex: 1, py: 1, px: 1.5 }}>
                {menuItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    return (
                        <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                            <ListItemButton
                                onClick={() => handleNavigate(item.path)}
                                selected={isActive}
                                sx={{
                                    borderRadius: '10px',
                                    py: 1,
                                    px: 1.5,
                                    '&.Mui-selected': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                        color: theme.palette.primary.dark,
                                        '&:hover': {
                                            backgroundColor: alpha(theme.palette.primary.main, 0.15),
                                        },
                                        '& .MuiListItemIcon-root': {
                                            color: theme.palette.primary.main,
                                        },
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            left: 0,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            width: 3,
                                            height: 20,
                                            borderRadius: '0 3px 3px 0',
                                            backgroundColor: theme.palette.primary.main,
                                        },
                                    },
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                                    },
                                }}
                            >
                                <ListItemIcon sx={{
                                    color: isActive ? theme.palette.primary.main : theme.palette.grey[500],
                                    minWidth: 38,
                                    '& .MuiSvgIcon-root': { fontSize: 20 },
                                }}>
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText
                                    primary={item.text}
                                    slotProps={{
                                        primary: {
                                            sx: {
                                                fontWeight: isActive ? 700 : 500,
                                                fontSize: '0.875rem',
                                                color: isActive ? theme.palette.primary.dark : theme.palette.text.primary,
                                            },
                                        },
                                    }}
                                />
                            </ListItemButton>
                        </ListItem>
                    );
                })}
            </List>

            {/* User profile section */}
            <Box sx={{ px: 1.5, pb: 2 }}>
                <Divider sx={{ mb: 1.5, opacity: 0.6 }} />
                <ListItemButton
                    onClick={handleMenuOpen}
                    sx={{
                        borderRadius: '10px',
                        py: 1,
                        px: 1.5,
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                    }}
                >
                    <ListItemIcon sx={{ minWidth: 38 }}>
                        <Avatar
                            sx={{
                                width: 32,
                                height: 32,
                                fontSize: '0.8rem',
                                bgcolor: alpha(theme.palette.primary.main, 0.12),
                                color: theme.palette.primary.dark,
                            }}
                            alt={user?.name}
                            src={user?.avatar || undefined}
                            slotProps={{ img: { referrerPolicy: 'no-referrer' } }}
                        >
                            {user?.name?.charAt(0)}
                        </Avatar>
                    </ListItemIcon>
                    <ListItemText
                        primary={`${isUserDoctor ? "Dr. " : ""}${user?.name}`}
                        secondary={isUserAdmin ? "Admin" : isUserPatient ? "Patient" : "Doctor"}
                        slotProps={{
                            primary: { sx: { fontWeight: 600, fontSize: '0.8rem' }, noWrap: true },
                            secondary: { sx: { fontSize: '0.7rem', color: theme.palette.text.secondary } },
                        }}
                    />
                </ListItemButton>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />

            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    width: { lg: `calc(100% - ${DRAWER_WIDTH}px)` },
                    ml: { lg: `${DRAWER_WIDTH}px` },
                    bgcolor: alpha(theme.palette.background.default, 0.8),
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    borderBottom: '1px solid',
                    borderColor: alpha(theme.palette.divider, 0.6),
                    color: 'text.primary',
                }}
            >
                <Toolbar sx={{ minHeight: '64px !important' }}>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { lg: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography variant="h6" noWrap sx={{
                        flexGrow: 1,
                        fontWeight: 700,
                        fontSize: '1rem',
                        color: theme.palette.text.primary,
                    }}>
                        {appTitle}
                    </Typography>

                    <IconButton onClick={handleMenuOpen} size="small" sx={{ ml: 2 }}>
                        <Avatar
                            sx={{
                                width: 34,
                                height: 34,
                                bgcolor: alpha(theme.palette.primary.main, 0.12),
                                color: theme.palette.primary.dark,
                                fontSize: '0.85rem',
                                border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            }}
                            src={user?.avatar || undefined}
                            slotProps={{ img: { referrerPolicy: 'no-referrer' } }}
                        >
                            {user?.name?.charAt(0)}
                        </Avatar>
                    </IconButton>
                </Toolbar>
            </AppBar>

            <Box component="nav" sx={{ width: { lg: DRAWER_WIDTH }, flexShrink: { lg: 0 } }}>
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        display: { xs: 'block', lg: 'none' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: DRAWER_WIDTH,
                            backgroundImage: 'none',
                            borderRight: '1px solid',
                            borderColor: alpha(theme.palette.divider, 0.5),
                        },
                    }}
                >
                    {drawer}
                </Drawer>

                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', lg: 'block' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: DRAWER_WIDTH,
                            borderRight: '1px solid',
                            borderColor: alpha(theme.palette.divider, 0.5),
                            backgroundImage: 'none',
                        },
                    }}
                    open
                >
                    {drawer}
                </Drawer>
            </Box>

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    width: { lg: `calc(100% - ${DRAWER_WIDTH}px)` },
                    minHeight: '100vh',
                    bgcolor: 'transparent',
                    overflowX: 'hidden',
                }}
            >
                <Toolbar sx={{ minHeight: '64px !important' }} />
                <Outlet />
            </Box>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                slotProps={{
                    paper: {
                        elevation: 0,
                        sx: {
                            minWidth: 200,
                            mt: 1.5,
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: alpha(theme.palette.divider, 0.6),
                            boxShadow: `0 10px 40px -10px ${alpha(theme.palette.common.black, 0.12)}`,
                        },
                    },
                }}
            >
                <MenuItem
                    onClick={() => { handleNavigate(profilePath); handleMenuClose(); }}
                    sx={{ py: 1.25, px: 2 }}
                >
                    <ListItemIcon><AccountCircle fontSize="small" /></ListItemIcon>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>My Profile</Typography>
                </MenuItem>
                <Divider sx={{ mx: 1 }} />
                <MenuItem onClick={handleLogout} sx={{ py: 1.25, px: 2, color: 'error.main' }}>
                    <ListItemIcon><Logout fontSize="small" color="error" /></ListItemIcon>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>Logout</Typography>
                </MenuItem>
            </Menu>
        </Box>
    );
};

export default Layout;
